/**
 * RES-44 — Transferencias de stock entre locales (épica D2): validación del formulario y armado
 * del body de `POST /inventory/transfers`.
 */

/** Máximo de líneas por transferencia (el backend responde `TOO_MANY` si se pasa). */
export const MAX_TRANSFER_LINES = 24;

let lineSeq = 0;

/**
 * Línea vacía del formulario (con una key estable para React).
 * @param {{itemId?: string, quantity?: string|number}} [init] - Valores iniciales
 * @returns {{key: string, itemId: string, quantity: string, available: number|null}} Línea
 */
export function newTransferLine(init = {}) {
  lineSeq += 1;
  return {
    key: `line-${lineSeq}`,
    itemId: init.itemId || '',
    quantity:
      init.quantity === undefined || init.quantity === null || init.quantity === ''
        ? ''
        : String(init.quantity),
    available: null,
  };
}

const parseQuantity = (value) => {
  if (value === '' || value === null || value === undefined) return NaN;
  return Number(String(value).replace(',', '.'));
};

/**
 * Valida el formulario antes de mandarlo.
 * @param {{fromRestaurantId: string, toRestaurantId: string, lines: Array}} form - Formulario
 * @returns {{form: string[], lines: Object<string, string>}} Claves de error (`form`) y error por
 *   línea (`lines[key]`): `from`, `to`, `same`, `noLines`, `tooMany`, `item`, `quantity`, `duplicate`
 */
export function validateTransfer({ fromRestaurantId, toRestaurantId, lines = [] }) {
  const form = [];
  const lineErrors = {};
  if (!fromRestaurantId) form.push('from');
  if (!toRestaurantId) form.push('to');
  if (fromRestaurantId && toRestaurantId && fromRestaurantId === toRestaurantId) form.push('same');
  if (lines.length === 0) form.push('noLines');
  if (lines.length > MAX_TRANSFER_LINES) form.push('tooMany');

  const seen = new Set();
  lines.forEach((line) => {
    if (!line.itemId) {
      lineErrors[line.key] = 'item';
      return;
    }
    if (seen.has(line.itemId)) {
      lineErrors[line.key] = 'duplicate';
      return;
    }
    seen.add(line.itemId);
    const quantity = parseQuantity(line.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) lineErrors[line.key] = 'quantity';
  });

  return { form, lines: lineErrors };
}

/** `true` si la validación no encontró nada. */
export const isTransferValid = (errors) =>
  errors.form.length === 0 && Object.keys(errors.lines).length === 0;

/**
 * Body del POST.
 * @param {{fromRestaurantId: string, toRestaurantId: string, lines: Array, reason?: string}} form
 * @returns {Object} `{ fromRestaurantId, toRestaurantId, items: [{itemId, quantity}], reason? }`
 */
export function buildTransferPayload({ fromRestaurantId, toRestaurantId, lines = [], reason }) {
  const body = {
    fromRestaurantId,
    toRestaurantId,
    items: lines.map((line) => ({ itemId: line.itemId, quantity: parseQuantity(line.quantity) })),
  };
  const trimmed = String(reason || '').trim();
  if (trimmed) body.reason = trimmed;
  return body;
}

/**
 * Marca en cada línea lo que hay disponible en el origen según el `INSUFFICIENT_STOCK`.
 * @param {Array} lines - Líneas del formulario
 * @param {Array<{itemId: string, available: number}>} shortages - Faltantes del backend
 * @returns {Array} Líneas con `available` (las que no faltan quedan en `null`)
 */
export function applyShortages(lines, shortages = []) {
  const byItem = new Map((shortages || []).map((s) => [s.itemId, s]));
  return lines.map((line) => {
    const shortage = byItem.get(line.itemId);
    return { ...line, available: shortage ? Number(shortage.available) || 0 : null };
  });
}

/**
 * Lista de locales de `listRestaurants()` (array o `{ restaurants }`), sin los eliminados.
 * @param {*} response - Respuesta
 * @returns {Array<{restaurantId: string, name: string}>} Locales
 */
export function normalizeRestaurantList(response) {
  const list = Array.isArray(response) ? response : (response && response.restaurants) || [];
  return list
    .filter((r) => r && r.restaurantId && r.status !== 'deleted')
    .map((r) => ({
      restaurantId: r.restaurantId,
      name: r.name || r.restaurantId,
      // `GET /inventory/transfers/restaurants` (RES-104) marca los locales que el usuario administra
      ...(typeof r.managed === 'boolean' ? { managed: r.managed } : {}),
    }));
}

/**
 * Indica si el usuario administra un local. Sin el dato (lista de `listRestaurants()`) se asume
 * que sí y el backend decide.
 * @param {Array<{restaurantId: string, managed?: boolean}>} restaurants - Locales
 * @param {string} restaurantId - Local
 * @returns {boolean} `true` si lo administra (o no se sabe)
 */
export function isManagedRestaurant(restaurants, restaurantId) {
  const restaurant = (restaurants || []).find((r) => r.restaurantId === restaurantId);
  return !restaurant || restaurant.managed !== false;
}

/**
 * Locales que se ofrecen de un lado de la transferencia según lo elegido del otro (RES-104): el
 * origen o el destino tiene que ser un local que el usuario administra, así que si el otro lado
 * es ajeno solo quedan los suyos.
 * @param {Array<{restaurantId: string, managed?: boolean}>} restaurants - Locales
 * @param {string} otherRestaurantId - Local elegido del otro lado (vacío si ninguno)
 * @returns {Array} Locales para el select
 */
export function transferCounterparts(restaurants, otherRestaurantId) {
  if (!otherRestaurantId || isManagedRestaurant(restaurants, otherRestaurantId)) {
    return restaurants;
  }
  return restaurants.filter((r) => r.managed !== false);
}

/**
 * Movimientos de una línea en el detalle (salida en origen y entrada en destino).
 * @param {Array<Object>} movements - `movements` de `GET /inventory/transfers/{id}`
 * @param {string} itemId - Insumo
 * @returns {{out: Object|null, in: Object|null}} Movimientos
 */
export function lineMovements(movements, itemId) {
  const forItem = (movements || []).filter((m) => m.ItemId === itemId);
  return {
    out: forItem.find((m) => m.Type === 'TRANSFER_OUT') || null,
    in: forItem.find((m) => m.Type === 'TRANSFER_IN') || null,
  };
}

/**
 * Cantidad corta (hasta 3 decimales).
 * @param {number} value - Cantidad
 * @returns {string} Texto
 */
export function formatTransferQuantity(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return String(Math.round(number * 1000) / 1000);
}
